/**
 * THE BUILDER — `tasks/patient-forms.md` §5.1.
 *
 * Sections and questions, drag-and-drop reorder of both, per-question configuration for every
 * §13.1 type, a one-level `showIf` editor, and a live preview.
 *
 * ── THREE THINGS THIS FILE DOES NOT DO, EACH ON PURPOSE ──────────────────────────────────────
 *
 * 1. **It does not implement a preview.** §5.1: "A builder whose preview is a second implementation
 *    is a builder that lies." The preview is `<app-dynamic-form-renderer>` — the SAME component the
 *    patient sees at `/patient-form/:token` and the same one the invite wizard splices in. A
 *    hand-rolled preview would be a third opinion about `showIf`, about which types render as what,
 *    and about whether a required-but-hidden question blocks submission.
 *
 * 2. **It does not implement a schema validator.** `validateSchema` from
 *    `shared/util/form-schema.util.ts` is the same function `utils/customFormSchema.js` mirrors and
 *    `scripts/verify-custom-form-validation.js` proves the two agree on. A second validator here
 *    would be a fourth opinion, and it would be the one that decided whether Publish was enabled.
 *
 * 3. **It does not treat a client-side pass as permission to publish.** The client check runs FIRST,
 *    so the practice sees its mistakes against the offending question instead of as a server
 *    round-trip. But a 400 `SCHEMA_INVALID` is rendered exactly as loudly, and `serverErrors` is a
 *    separate field from `clientErrors` precisely so a local pass can never blank one out.
 *
 * ── THE `showIf` RULE THAT MAKES THIS A BUILDER RATHER THAN A TRAP ───────────────────────────
 *
 * `validateSchema` rejects a `showIf` that names a question at or after its own position, because
 * such a form cannot be completed: the patient would have to answer a question they cannot see in
 * order to see it. A builder that offers a later question in the picker has produced that form, and
 * the practice does not find out until Publish.
 *
 * So the picker offers only EARLIER questions (`controllingQuestions`), and — because a drag-and-drop
 * reorder can break a condition that was legal when it was written — every structural change
 * recomputes `brokenConditions` and the UI says so at the top of the builder and against the
 * question itself. Reordering is never blocked (a practice mid-restructure would be stuck); it is
 * SURFACED, which is the difference between a builder that argues with you and one that lies to you.
 */

import {
  ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Subscription, debounceTime, takeUntil } from 'rxjs';

import { BaseComponent } from '../../../../core/components/base/base.component';
import { FeedbackService } from '../../../../core/services/feedback.service';
import {
  CustomFormDto, CustomFormService, CustomFormVersionDto,
} from '../../../../core/services/custom-form.service';
import {
  FormAnswers, FormOption, FormQuestion, FormQuestionType, FormSchema, FormSection,
} from '../../../../shared/models/custom-form.model';
import { validateSchema } from '../../../../shared/util/form-schema.util';

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * The type vocabulary, once
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export interface QuestionTypeChoice {
  value: FormQuestionType;
  label: string;
  hint: string;
}

/** Every §13.1 type. The order is the order the practice sees. */
export const QUESTION_TYPE_CHOICES: QuestionTypeChoice[] = [
  { value: 'text', label: 'Short text', hint: 'One line of free text.' },
  { value: 'textarea', label: 'Long text', hint: 'A paragraph.' },
  { value: 'number', label: 'Number', hint: 'A number, optionally within a range.' },
  { value: 'date', label: 'Date', hint: 'A calendar date.' },
  { value: 'radio', label: 'Choose one', hint: 'Options shown as buttons; exactly one.' },
  { value: 'checkbox', label: 'Choose any', hint: 'Options shown as tickboxes; any number.' },
  { value: 'select', label: 'Dropdown', hint: 'Options in a list; exactly one.' },
  { value: 'yesno', label: 'Yes / No', hint: 'Two buttons, with no default.' },
  { value: 'scale', label: 'Scale', hint: 'Numbered points, e.g. 1 to 5.' },
  { value: 'signature', label: 'Signature', hint: 'The patient signs on screen.' },
  { value: 'statictext', label: 'Information', hint: 'Text to read. No answer is collected.' },
];

/** `radio` | `checkbox` | `select` — the types `validateSchema` requires options for. */
export const OPTION_QUESTION_TYPES: FormQuestionType[] = ['radio', 'checkbox', 'select'];

/** `number` | `scale` — the types that use `min` / `max`. */
export const RANGE_QUESTION_TYPES: FormQuestionType[] = ['number', 'scale'];

export interface ShowIfOperatorChoice {
  value: 'eq' | 'ne' | 'in' | 'gt' | 'lt' | 'answered';
  label: string;
  /** Whether the operator takes a value at all — `answered` does not. */
  takesValue: boolean;
  /** Whether the value is a LIST (`in`) rather than a single value. */
  multiple: boolean;
}

export const SHOW_IF_OPERATOR_CHOICES: ShowIfOperatorChoice[] = [
  { value: 'eq', label: 'is', takesValue: true, multiple: false },
  { value: 'ne', label: 'is not', takesValue: true, multiple: false },
  { value: 'in', label: 'is any of', takesValue: true, multiple: true },
  { value: 'gt', label: 'is more than', takesValue: true, multiple: false },
  { value: 'lt', label: 'is less than', takesValue: true, multiple: false },
  { value: 'answered', label: 'has been answered', takesValue: false, multiple: false },
];

/** One entry in the `showIf` picker: a question that appears strictly EARLIER. */
export interface ControllingQuestion {
  id: string;
  label: string;
  type: FormQuestionType;
  options: FormOption[];
}

/**
 * What the builder is told when the questions in it came out of a PDF (§16).
 *
 * Its only purpose is to make the draft's PROVENANCE visible: these questions were read by a
 * machine, no person has checked them, and nothing is published. The builder does not treat such a
 * schema differently in any other way — it is an ordinary draft, and the practice edits and
 * publishes it through exactly the same controls as one they typed.
 */
export interface ImportedDraftNotice {
  importId: string;
  sourceFileName: string;
  /** §16.2 rule 4 — the original, so a reviewer can compare question by question. */
  sourceFileUrl: string | null;
  pageCount: number | null;
  /** §16.2 rule 2 — `validateSchema`'s verdict, as the SERVER stored it beside the draft. */
  schemaErrors: string[];
}

/** A `showIf` that a reorder (or an edit) has made impossible. */
export interface BrokenCondition {
  questionId: string;
  questionLabel: string;
  referencedId: string;
  message: string;
}

@Component({
  selector: 'app-custom-form-builder',
  templateUrl: './custom-form-builder.component.html',
  styleUrls: ['./custom-form-builder.component.scss'],
})
export class CustomFormBuilderComponent extends BaseComponent
  implements OnInit, OnChanges, OnDestroy {

  /** The form being edited. Its id is where a publish goes; its name titles the preview. */
  @Input() form: CustomFormDto | null = null;

  /**
   * The schema to start from — the form's current version, a version copied out of history, or a
   * duplicated form's source. `null` seeds one empty section, so a new form opens on something to
   * type into rather than on a blank page with a button.
   */
  @Input() initialSchema: FormSchema | null = null;

  /**
   * Set when `initialSchema` came out of a PDF extraction (§16).
   *
   * `null` for everything else — a form the practice typed, a version copied out of history, a
   * duplicate. When it is set the builder says so at the top, in as many words, and shows the
   * server's stored `validateSchema` problems against the questions they belong to.
   */
  @Input() importedDraft: ImportedDraftNotice | null = null;

  /** Emitted after a successful publish, with the version the server actually wrote. */
  @Output() published = new EventEmitter<{ version: CustomFormVersionDto; form: CustomFormDto }>();

  /** Emitted whenever the edited schema changes, so a host can warn about leaving. */
  @Output() dirtyChange = new EventEmitter<boolean>();

  builderForm!: FormGroup;

  /** The schema handed to the renderer. Recomputed off `valueChanges`, debounced. */
  previewSchema: FormSchema | null = null;

  /** The preview's own answers. Throwaway — nothing here is ever submitted anywhere. */
  previewAnswers: FormAnswers = {};

  showPreview = true;
  isPublishing = false;
  isDirty = false;

  /** `validateSchema`'s messages, run locally before the request. */
  clientErrors: string[] = [];

  /**
   * The server's `details.errors` from a 400 `SCHEMA_INVALID`.
   *
   * A SEPARATE field from `clientErrors`, never merged into it. The whole failure mode this guards
   * against is a client-side pass suppressing a server refusal, and one field holding both would
   * make "cleared because the local check passed" indistinguishable from "the server was happy".
   */
  serverErrors: string[] = [];

  /** A non-schema refusal (404, 409, 500, a dropped connection). */
  publishError: string | null = null;

  /** Every problem `validateSchema` could attribute to a specific question, keyed by question id. */
  errorsByQuestion: { [questionId: string]: string[] } = {};

  /** Problems that named no question — a missing section title, an empty form. */
  generalErrors: string[] = [];

  /** Conditions a reorder or an edit has made impossible. Recomputed on every structural change. */
  brokenConditions: BrokenCondition[] = [];

  /** Set by a drop, cleared by the next structural change, so the reason is attached to the cause. */
  reorderWarning: string | null = null;

  /**
   * The IMPORT's stored problems, attributed to questions — kept apart from `errorsByQuestion` for
   * the same reason `serverErrors` is kept apart from `clientErrors`: they are somebody else's
   * verdict, taken at a known moment, and merging them would make "the extraction complained about
   * this" indistinguishable from "this is wrong right now".
   */
  importErrorsByQuestion: { [questionId: string]: string[] } = {};

  /** Import problems that named no question. */
  importGeneralErrors: string[] = [];

  /**
   * Each question exactly as the extraction produced it, by id.
   *
   * The extraction's complaint about a question stands until the practice CHANGES that question —
   * at which point it is stale and is dropped. Without this, a stored error would either hang
   * around beside a question that has been fixed, or be cleared wholesale the first time any
   * unrelated field was touched. Both are lies about what the server said.
   */
  private importedQuestionSnapshot: { [questionId: string]: string } = {};

  readonly questionTypes = QUESTION_TYPE_CHOICES;
  readonly operators = SHOW_IF_OPERATOR_CHOICES;

  /** Ids for the cdkDropList connections — one per section's question list. */
  get questionListIds(): string[] {
    return this.sections.controls.map((_, index) => `question-list-${index}`);
  }

  private valueSub?: Subscription;

  constructor(
    private formBuilder: FormBuilder,
    private customFormService: CustomFormService,
    private cdr: ChangeDetectorRef,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Lifecycle
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  ngOnInit(): void {
    if (!this.builderForm) this.rebuild();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // A new schema means a different form (or a version copied out of history) — start over rather
    // than merging, which would leave half of one form's questions inside another's.
    if (changes['initialSchema'] || changes['form'] || changes['importedDraft']) {
      const formChanged = changes['form']
        && (!changes['form'].previousValue
          || !changes['form'].currentValue
          || changes['form'].previousValue.id !== changes['form'].currentValue.id);
      if (changes['initialSchema'] || formChanged) {
        // `rebuild` reads `importedDraft` itself, and Angular has already written every input by
        // the time `ngOnChanges` runs — so a schema and its provenance always arrive together.
        this.rebuild();
        return;
      }
      if (changes['importedDraft']) this.applyImportedDraft();
    }
  }

  override ngOnDestroy(): void {
    this.valueSub?.unsubscribe();
    super.ngOnDestroy();
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Construction
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  private rebuild(): void {
    this.valueSub?.unsubscribe();

    const schema = this.initialSchema;
    const sections = schema && Array.isArray(schema.sections) && schema.sections.length > 0
      ? schema.sections
      : [{ id: this.generateId('sec'), title: 'Section 1', description: '', questions: [] }];

    this.builderForm = this.formBuilder.group({
      sections: this.formBuilder.array(sections.map((s) => this.createSectionFormGroup(s))),
    });

    this.isDirty = false;
    this.clientErrors = [];
    this.serverErrors = [];
    this.publishError = null;
    this.errorsByQuestion = {};
    this.generalErrors = [];
    this.reorderWarning = null;
    this.previewAnswers = {};
    this.applyImportedDraft();

    /*
     * The preview is rebuilt off `valueChanges` rather than on every keystroke synchronously: the
     * renderer rebuilds its controls when its `[schema]` identity changes, so an un-debounced
     * rebuild would reset the preview's own answers while the author typed a question's text.
     */
    this.valueSub = this.builderForm.valueChanges
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe(() => {
        this.isDirty = true;
        this.dirtyChange.emit(true);
        this.refreshDerivedState();
      });

    this.refreshDerivedState();
  }

  private createSectionFormGroup(section: Partial<FormSection>): FormGroup {
    return this.formBuilder.group({
      id: [section.id || this.generateId('sec')],
      title: [section.title || '', Validators.required],
      description: [section.description || ''],
      questions: this.formBuilder.array(
        (section.questions || []).map((q) => this.createQuestionFormGroup(q)),
      ),
    });
  }

  /**
   * Every §13.1 field is a control, present regardless of type.
   *
   * The survey builder adds and removes an `options` array as the type changes; this one keeps the
   * array and lets `buildSchema` decide what reaches the schema. That is what lets an author flip
   * radio → text → radio without losing the options they already typed, and it removes a whole class
   * of "control does not exist" template errors under `strictTemplates`.
   */
  private createQuestionFormGroup(question: Partial<FormQuestion>): FormGroup {
    const isScale = question.type === 'scale';
    return this.formBuilder.group({
      id: [question.id || this.generateId('q')],
      type: [question.type || 'text', Validators.required],
      question: [question.question || '', Validators.required],
      required: [question.required === true],
      placeholder: [question.placeholder || ''],
      helpText: [question.helpText || ''],
      min: [question.min === undefined || question.min === null ? (isScale ? 1 : null) : question.min],
      max: [question.max === undefined || question.max === null ? (isScale ? 5 : null) : question.max],
      options: this.formBuilder.array(
        (question.options || []).map((o) => this.createOptionFormGroup(o)),
      ),
      showIf: this.createShowIfFormGroup(question),
    });
  }

  private createOptionFormGroup(option: Partial<FormOption>): FormGroup {
    return this.formBuilder.group({
      value: [option.value || '', Validators.required],
      text: [option.text || '', Validators.required],
      // Blank, not 0. §13.1: a form with no `score` anywhere scores null/null, and "unscored" and
      // "scored zero" are different facts — a default of 0 would quietly make every form scored.
      score: [option.score === undefined || option.score === null ? null : option.score],
    });
  }

  /**
   * `enabled` is a control rather than a null `showIf`: a checkbox bound to a group's existence
   * cannot be unticked without destroying what the author typed, so unticking it here keeps the
   * condition around to be reticked, and `buildSchema` simply does not emit it.
   */
  private createShowIfFormGroup(question: Partial<FormQuestion>): FormGroup {
    const showIf = question.showIf;
    const isList = !!showIf && showIf.operator === 'in';
    return this.formBuilder.group({
      enabled: [!!showIf],
      questionId: [showIf ? showIf.questionId : ''],
      operator: [showIf ? showIf.operator : 'eq'],
      // One control for a single value and one for a list, so switching operator does not have to
      // reinterpret a string as an array in the middle of an edit.
      value: [showIf && !isList && showIf.value !== undefined ? String(showIf.value) : ''],
      values: [isList && Array.isArray(showIf!.value) ? (showIf!.value as string[]) : []],
    });
  }

  private generateId(prefix: string): string {
    return `${prefix}_${Math.random().toString(36).slice(2, 11)}`;
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Form-array accessors — the template's read surface
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  get sections(): FormArray {
    return this.builderForm.get('sections') as FormArray;
  }

  sectionAt(sectionIndex: number): FormGroup {
    return this.sections.at(sectionIndex) as FormGroup;
  }

  questionsIn(sectionIndex: number): FormArray {
    return this.sectionAt(sectionIndex).get('questions') as FormArray;
  }

  questionAt(sectionIndex: number, questionIndex: number): FormGroup {
    return this.questionsIn(sectionIndex).at(questionIndex) as FormGroup;
  }

  optionsIn(sectionIndex: number, questionIndex: number): FormArray {
    return this.questionAt(sectionIndex, questionIndex).get('options') as FormArray;
  }

  showIfGroup(sectionIndex: number, questionIndex: number): FormGroup {
    return this.questionAt(sectionIndex, questionIndex).get('showIf') as FormGroup;
  }

  /** `strictTemplates` will not narrow an `AbstractControl` on its own. */
  asFormGroup(control: AbstractControl | null): FormGroup {
    return control as FormGroup;
  }

  controlValue(control: AbstractControl | null, path: string): any {
    return control ? control.get(path)?.value : undefined;
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Type-driven UI
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  usesOptions(type: FormQuestionType): boolean {
    return OPTION_QUESTION_TYPES.indexOf(type) !== -1;
  }

  usesRange(type: FormQuestionType): boolean {
    return RANGE_QUESTION_TYPES.indexOf(type) !== -1;
  }

  usesPlaceholder(type: FormQuestionType): boolean {
    return type === 'text' || type === 'textarea' || type === 'number';
  }

  /** `statictext` collects no answer, so it can never be required — `validateSchema` says so too. */
  canBeRequired(type: FormQuestionType): boolean {
    return type !== 'statictext';
  }

  operatorChoice(operator: string): ShowIfOperatorChoice | undefined {
    return this.operators.filter((o) => o.value === operator)[0];
  }

  typeLabel(type: FormQuestionType): string {
    const found = this.questionTypes.filter((t) => t.value === type)[0];
    return found ? found.label : String(type);
  }

  /**
   * Switching type keeps everything the author typed, but seeds a `scale` with 1-5 so the preview
   * has something to draw, and gives an options type a first empty row rather than the "no options
   * to choose from" error the moment the type changes.
   */
  onQuestionTypeChange(sectionIndex: number, questionIndex: number): void {
    const group = this.questionAt(sectionIndex, questionIndex);
    const type = group.get('type')!.value as FormQuestionType;

    if (type === 'scale') {
      if (group.get('min')!.value === null || group.get('min')!.value === '') {
        group.get('min')!.setValue(1, { emitEvent: false });
      }
      if (group.get('max')!.value === null || group.get('max')!.value === '') {
        group.get('max')!.setValue(5, { emitEvent: false });
      }
    }

    if (this.usesOptions(type) && this.optionsIn(sectionIndex, questionIndex).length === 0) {
      this.addOption(sectionIndex, questionIndex);
    }

    // A statictext cannot be required, and leaving the flag set would publish an unfillable form.
    if (type === 'statictext') group.get('required')!.setValue(false, { emitEvent: false });

    this.markStructuralChange();
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Structure — add, remove, reorder
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  addSection(): void {
    this.sections.push(this.createSectionFormGroup({
      id: this.generateId('sec'),
      title: `Section ${this.sections.length + 1}`,
      description: '',
      questions: [],
    }));
    this.markStructuralChange();
  }

  async removeSection(sectionIndex: number): Promise<void> {
    const questionCount = this.questionsIn(sectionIndex).length;
    const title = this.sectionAt(sectionIndex).get('title')!.value || `Section ${sectionIndex + 1}`;
    const confirmed = await this.showConfirmationDialog(
      'Remove section',
      `Remove "${title}"${questionCount ? ` and its ${questionCount} question(s)` : ''}? `
      + 'Published versions are unaffected — this only changes the draft you are editing.',
    );
    if (!confirmed) return;
    this.sections.removeAt(sectionIndex);
    this.markStructuralChange();
  }

  addQuestion(sectionIndex: number, type: FormQuestionType = 'text'): void {
    this.questionsIn(sectionIndex).push(this.createQuestionFormGroup({
      id: this.generateId('q'),
      type,
      question: '',
      required: false,
    }));
    this.markStructuralChange();
  }

  removeQuestion(sectionIndex: number, questionIndex: number): void {
    const id = this.questionAt(sectionIndex, questionIndex).get('id')!.value;
    this.questionsIn(sectionIndex).removeAt(questionIndex);
    // Removing a question can orphan a condition that pointed at it, which `brokenConditions`
    // reports as "no longer on this form" rather than as a forward reference.
    this.markStructuralChange(this.dependentsOf(id).length > 0
      ? `Removing that question left ${this.dependentsOf(id).length} display condition(s) pointing at a question that is no longer on the form.`
      : null);
  }

  addOption(sectionIndex: number, questionIndex: number): void {
    const options = this.optionsIn(sectionIndex, questionIndex);
    const next = options.length + 1;
    options.push(this.createOptionFormGroup({ value: `option_${next}`, text: '', score: null as any }));
    this.markStructuralChange();
  }

  removeOption(sectionIndex: number, questionIndex: number, optionIndex: number): void {
    this.optionsIn(sectionIndex, questionIndex).removeAt(optionIndex);
    this.markStructuralChange();
  }

  /**
   * SECTION REORDER.
   *
   * Moving a section moves every question in it, so this is the drop most likely to invert a pair of
   * questions that are related by a `showIf`. The reorder is applied and then reported — see the
   * header for why it is not refused.
   */
  onSectionDrop(event: CdkDragDrop<any>): void {
    if (event.previousIndex === event.currentIndex) return;
    const moved = this.sections.at(event.previousIndex);
    this.sections.removeAt(event.previousIndex);
    this.sections.insert(event.currentIndex, moved);
    this.afterReorder('Moving that section changed the order questions are asked in.');
  }

  /**
   * QUESTION REORDER, within a section or between two of them.
   *
   * `cdkDropList` gives the source list's data in `event.previousContainer.data`; both are the
   * section INDEX, which is stable for the duration of a single drop.
   */
  onQuestionDrop(event: CdkDragDrop<any, any>, sectionIndex: number): void {
    const fromSection = Number(event.previousContainer.data);
    const toSection = sectionIndex;

    if (fromSection === toSection && event.previousIndex === event.currentIndex) return;

    const source = this.questionsIn(fromSection);
    const target = this.questionsIn(toSection);
    const moved = source.at(event.previousIndex);
    source.removeAt(event.previousIndex);
    target.insert(event.currentIndex, moved);

    this.afterReorder(fromSection === toSection
      ? 'Moving that question changed the order questions are asked in.'
      : 'Moving that question to another section changed the order questions are asked in.');
  }

  /**
   * The point of the whole reorder story.
   *
   * A drop is applied, then re-validated. If it broke a condition the author is told immediately and
   * specifically — which question, and which question it now depends on — rather than at Publish,
   * three edits later, as a wall of text from the server.
   */
  private afterReorder(cause: string): void {
    this.markStructuralChange();
    if (this.brokenConditions.length > 0) {
      this.reorderWarning = `${cause} ${this.brokenConditions.length} display condition(s) no longer `
        + 'work: a question can only depend on an answer given earlier. Fix them below, or move the '
        + 'question back.';
    } else {
      this.reorderWarning = null;
    }
  }

  /**
   * Every add / remove / drop / type change funnels through here.
   *
   * `valueChanges` is debounced, so a structural change would otherwise leave the picker offering a
   * stale list of controlling questions for up to 200ms — long enough to click.
   */
  private markStructuralChange(warning: string | null = null): void {
    this.isDirty = true;
    this.dirtyChange.emit(true);
    this.reorderWarning = warning;
    this.refreshDerivedState();
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * The `showIf` editor
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /**
   * The ONLY questions the picker may offer: those strictly EARLIER in document order.
   *
   * `statictext` is excluded as well as later questions. It collects no answer (§13.1), so every
   * operator against one is either always false or meaningless — `validateSchema` permits it, and a
   * form built on it renders a question that can never appear. Offering it would be offering a trap
   * the validator does not catch.
   */
  controllingQuestions(sectionIndex: number, questionIndex: number): ControllingQuestion[] {
    const result: ControllingQuestion[] = [];

    for (let s = 0; s < this.sections.length; s++) {
      const questions = this.questionsIn(s);
      for (let q = 0; q < questions.length; q++) {
        // Strictly earlier: same section and same index is the question itself, and
        // `validateSchema` calls a self-reference "shown based on its own answer".
        if (s > sectionIndex || (s === sectionIndex && q >= questionIndex)) return result;

        const group = questions.at(q) as FormGroup;
        const type = group.get('type')!.value as FormQuestionType;
        if (type === 'statictext') continue;

        const id = group.get('id')!.value;
        const text = group.get('question')!.value;
        result.push({
          id,
          label: text && String(text).trim() !== '' ? String(text) : `(untitled ${this.typeLabel(type)})`,
          type,
          options: this.readOptions(group),
        });
      }
    }

    return result;
  }

  /** The options of the question a condition points at, so the value picker can be a list. */
  showIfTargetOptions(sectionIndex: number, questionIndex: number): FormOption[] {
    const targetId = this.showIfGroup(sectionIndex, questionIndex).get('questionId')!.value;
    if (!targetId) return [];
    const target = this.controllingQuestions(sectionIndex, questionIndex)
      .filter((c) => c.id === targetId)[0];
    return target ? target.options : [];
  }

  /** `yesno` has no options but has exactly two answers, and they are the strings §13.5 fixes. */
  showIfTargetIsYesNo(sectionIndex: number, questionIndex: number): boolean {
    const targetId = this.showIfGroup(sectionIndex, questionIndex).get('questionId')!.value;
    const target = this.controllingQuestions(sectionIndex, questionIndex)
      .filter((c) => c.id === targetId)[0];
    return !!target && target.type === 'yesno';
  }

  onShowIfToggled(sectionIndex: number, questionIndex: number): void {
    const group = this.showIfGroup(sectionIndex, questionIndex);
    if (group.get('enabled')!.value && !group.get('questionId')!.value) {
      const available = this.controllingQuestions(sectionIndex, questionIndex);
      // Default to the question immediately before this one — the overwhelmingly common intent, and
      // it is by construction a legal target.
      if (available.length > 0) {
        group.get('questionId')!.setValue(available[available.length - 1].id, { emitEvent: false });
      }
    }
    this.markStructuralChange();
  }

  onShowIfChanged(): void {
    this.markStructuralChange();
  }

  /** True when this question has nothing earlier to depend on — the editor says so instead of hiding. */
  hasNoControllingQuestions(sectionIndex: number, questionIndex: number): boolean {
    return this.controllingQuestions(sectionIndex, questionIndex).length === 0;
  }

  /** The broken condition attached to a question, for the inline message. */
  brokenConditionFor(questionId: string): BrokenCondition | null {
    return this.brokenConditions.filter((b) => b.questionId === questionId)[0] || null;
  }

  /** Which questions depend on this one — used when removing a question. */
  private dependentsOf(questionId: string): string[] {
    const dependents: string[] = [];
    for (const entry of this.flatten()) {
      const showIf = entry.group.get('showIf') as FormGroup;
      if (showIf.get('enabled')!.value && showIf.get('questionId')!.value === questionId) {
        dependents.push(entry.group.get('id')!.value);
      }
    }
    return dependents;
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Schema construction — the form tree becomes a §13.1 `FormSchema`
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /**
   * The schema this builder would publish.
   *
   * Optional keys are OMITTED rather than emitted empty: `placeholder: ''` and `showIf: null` are
   * noise in an immutable, encrypted record that other code reads for years. `options` and
   * `min`/`max` reach the schema only for the types that use them, so flipping a radio to a text
   * question does not publish a text question carrying five orphaned options.
   */
  buildSchema(): FormSchema {
    const sections: FormSection[] = [];

    for (let s = 0; s < this.sections.length; s++) {
      const sectionGroup = this.sectionAt(s);
      const questions: FormQuestion[] = [];
      const questionArray = this.questionsIn(s);

      for (let q = 0; q < questionArray.length; q++) {
        questions.push(this.buildQuestion(questionArray.at(q) as FormGroup));
      }

      const section: FormSection = {
        id: sectionGroup.get('id')!.value,
        title: this.trimmed(sectionGroup.get('title')!.value),
        questions,
      };
      const description = this.trimmed(sectionGroup.get('description')!.value);
      if (description) section.description = description;

      sections.push(section);
    }

    return { schemaVersion: 1, sections };
  }

  private buildQuestion(group: FormGroup): FormQuestion {
    const type = group.get('type')!.value as FormQuestionType;

    const question: FormQuestion = {
      id: group.get('id')!.value,
      type,
      question: this.trimmed(group.get('question')!.value),
    };

    if (group.get('required')!.value === true && this.canBeRequired(type)) question.required = true;

    if (this.usesPlaceholder(type)) {
      const placeholder = this.trimmed(group.get('placeholder')!.value);
      if (placeholder) question.placeholder = placeholder;
    }

    const helpText = this.trimmed(group.get('helpText')!.value);
    if (helpText) question.helpText = helpText;

    if (this.usesOptions(type)) question.options = this.readOptions(group);

    if (this.usesRange(type)) {
      const min = this.numberOrNull(group.get('min')!.value);
      const max = this.numberOrNull(group.get('max')!.value);
      if (min !== null) question.min = min;
      if (max !== null) question.max = max;
    }

    const showIf = this.buildShowIf(group);
    if (showIf) question.showIf = showIf;

    return question;
  }

  private readOptions(group: FormGroup): FormOption[] {
    const array = group.get('options') as FormArray;
    const options: FormOption[] = [];
    for (let i = 0; i < array.length; i++) {
      const optionGroup = array.at(i) as FormGroup;
      const value = this.trimmed(optionGroup.get('value')!.value);
      const text = this.trimmed(optionGroup.get('text')!.value);
      const option: FormOption = { value, text: text || value };
      // Emitted only when the author actually typed one. A blank stays absent, which is what keeps
      // an unscored form scoring null/null rather than 0/0 (§13.1).
      const score = this.numberOrNull(optionGroup.get('score')!.value);
      if (score !== null) option.score = score;
      options.push(option);
    }
    return options;
  }

  private buildShowIf(group: FormGroup): FormQuestion['showIf'] | null {
    const showIf = group.get('showIf') as FormGroup;
    if (showIf.get('enabled')!.value !== true) return null;

    const questionId = this.trimmed(showIf.get('questionId')!.value);
    if (!questionId) return null;

    const operator = showIf.get('operator')!.value as 'eq' | 'ne' | 'in' | 'gt' | 'lt' | 'answered';
    const choice = this.operatorChoice(operator);

    if (!choice || !choice.takesValue) return { questionId, operator };

    if (choice.multiple) {
      /*
       * `in` takes a LIST. A controlling question WITH options gets a multi-select bound to
       * `values`; one without (a free-text or number question) gets a plain input bound to `value`,
       * because a multi-select over nothing is an empty dropdown. Both land here, and the array is
       * what §13.1 says `in` carries either way.
       */
      const values = showIf.get('values')!.value;
      if (Array.isArray(values) && values.length > 0) {
        return { questionId, operator, value: values.map(String) };
      }
      const typed = this.trimmed(showIf.get('value')!.value);
      const split = typed === ''
        ? []
        : typed.split(',').map((part) => part.trim()).filter((part) => part !== '');
      return { questionId, operator, value: split };
    }

    const raw = showIf.get('value')!.value;
    if (operator === 'gt' || operator === 'lt') {
      // `gt`/`lt` are numeric comparisons. Emitting a number rather than a string keeps the schema
      // honest about what it means, and the util coerces either way.
      const asNumber = this.numberOrNull(raw);
      return { questionId, operator, value: asNumber === null ? String(raw ?? '') : asNumber };
    }

    return { questionId, operator, value: raw === null || raw === undefined ? '' : String(raw) };
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Derived state — validation, inline errors, preview
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /**
   * Re-derive everything that depends on the schema: `validateSchema`'s verdict mapped onto the
   * questions it names, the list of impossible conditions, and the preview's schema.
   */
  private refreshDerivedState(): void {
    const schema = this.buildSchema();

    this.brokenConditions = this.detectBrokenConditions(schema);

    const result = validateSchema(schema);
    this.clientErrors = result.errors;
    this.applyErrorAttribution(result.errors);
    this.pruneImportErrors(schema, result.errors);

    // The renderer tolerates a half-built schema by rendering what it can, so the preview updates as
    // the author types rather than only once the form is publishable.
    this.previewSchema = schema;

    this.cdr.markForCheck();
  }

  /**
   * Forward, self and dangling references, found DIRECTLY rather than by reading `validateSchema`'s
   * prose.
   *
   * `validateSchema` returns flat strings with no question id attached (see `applyErrorAttribution`),
   * which is fine for a published-form error list and not enough to put a message beside the right
   * `showIf` editor and offer a fix. Walking document order here is a handful of lines and gives the
   * UI the id, the referenced id and the reason. It never DECIDES anything: `validateSchema` remains
   * the only gate on Publish.
   */
  private detectBrokenConditions(schema: FormSchema): BrokenCondition[] {
    const ordered: FormQuestion[] = [];
    for (const section of schema.sections) {
      for (const question of section.questions) ordered.push(question);
    }

    const positionById: { [id: string]: number } = Object.create(null);
    ordered.forEach((question, index) => {
      if (positionById[question.id] === undefined) positionById[question.id] = index;
    });

    const broken: BrokenCondition[] = [];

    ordered.forEach((question, index) => {
      const showIf = question.showIf;
      if (!showIf || !showIf.questionId) return;

      const label = question.question && question.question.trim() !== ''
        ? question.question
        : `(untitled ${this.typeLabel(question.type)})`;

      if (showIf.questionId === question.id) {
        broken.push({
          questionId: question.id,
          questionLabel: label,
          referencedId: showIf.questionId,
          message: 'This question is shown based on its own answer, so it can never appear.',
        });
        return;
      }

      const target = positionById[showIf.questionId];
      if (target === undefined) {
        broken.push({
          questionId: question.id,
          questionLabel: label,
          referencedId: showIf.questionId,
          message: 'This question depends on a question that is no longer on the form.',
        });
        return;
      }

      if (target >= index) {
        const targetLabel = ordered[target].question || showIf.questionId;
        broken.push({
          questionId: question.id,
          questionLabel: label,
          referencedId: showIf.questionId,
          message: `This question depends on "${targetLabel}", which now comes later on the form. `
            + 'A question can only depend on an answer given earlier.',
        });
      }
    });

    return broken;
  }

  /**
   * Attribute each of `validateSchema`'s messages to a question where it names one.
   *
   * ── A ROUGH EDGE IN §13.4, NAMED RATHER THAN WORKED AROUND SILENTLY ─────────────────────────
   * `validateSchema` returns `{ valid, errors: string[] }` — prose written for a practice user, with
   * no `questionId` field. Its messages are prefixed `Question "<id>"` when they concern one, so
   * this reads the id back out of the prose. That is a parse of another module's user-facing text,
   * and it degrades gracefully (an unattributable message goes to `generalErrors` and is shown at
   * the top), but a `{ questionId, message }` shape would remove the parse entirely. Reported.
   */
  private applyErrorAttribution(errors: string[]): void {
    const byQuestion: { [questionId: string]: string[] } = {};
    const general: string[] = [];

    for (const error of errors) {
      const match = /^Question "([^"]+)"/.exec(error);
      if (match) {
        const id = match[1];
        if (!byQuestion[id]) byQuestion[id] = [];
        byQuestion[id].push(error);
      } else {
        general.push(error);
      }
    }

    this.errorsByQuestion = byQuestion;
    this.generalErrors = general;
  }

  /** The inline error list for one question — client errors and any the server attributed to it. */
  errorsFor(questionId: string): string[] {
    return this.errorsByQuestion[questionId] || [];
  }

  hasErrors(questionId: string): boolean {
    return this.errorsFor(questionId).length > 0
      || this.importErrorsFor(questionId).length > 0
      || !!this.brokenConditionFor(questionId);
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * The imported draft (§16)
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /**
   * Take the extraction's stored verdict apart ONCE, when the draft arrives.
   *
   * §16.2 rule 2 stores `validateSchema`'s errors beside the draft so the practice can repair it
   * rather than pay for a second extraction. Repairing means knowing WHICH question, so the same
   * `Question "<id>"` prefix the local errors are attributed by is used here — with the same
   * caveat recorded on `applyErrorAttribution`: it is a parse of another module's user-facing prose.
   */
  private applyImportedDraft(): void {
    this.importErrorsByQuestion = {};
    this.importGeneralErrors = [];
    this.importedQuestionSnapshot = {};

    const notice = this.importedDraft;
    if (!notice || !this.builderForm) return;

    for (const section of this.buildSchema().sections) {
      for (const question of section.questions) {
        this.importedQuestionSnapshot[question.id] = JSON.stringify(question);
      }
    }

    for (const error of notice.schemaErrors || []) {
      const match = /^Question "([^"]+)"/.exec(error);
      if (match) {
        const id = match[1];
        if (!this.importErrorsByQuestion[id]) this.importErrorsByQuestion[id] = [];
        this.importErrorsByQuestion[id].push(error);
      } else {
        this.importGeneralErrors.push(error);
      }
    }
  }

  /**
   * Drop an extraction's complaint once it can no longer be true.
   *
   * Two ways that happens, and only two: the question has been edited since it was imported (so the
   * server's verdict is about something that no longer exists), or the local `validateSchema` —
   * literally the same function the server ran — now passes on the whole schema. Anything else
   * keeps the message, because the practice has not addressed it yet.
   */
  private pruneImportErrors(schema: FormSchema, localErrors: string[]): void {
    if (this.importGeneralErrors.length === 0
      && Object.keys(this.importErrorsByQuestion).length === 0) return;

    if (localErrors.length === 0) {
      this.importErrorsByQuestion = {};
      this.importGeneralErrors = [];
      return;
    }

    const current: { [id: string]: string } = Object.create(null);
    for (const section of schema.sections) {
      for (const question of section.questions) current[question.id] = JSON.stringify(question);
    }

    for (const id of Object.keys(this.importErrorsByQuestion)) {
      const snapshot = this.importedQuestionSnapshot[id];
      if (current[id] === undefined || current[id] !== snapshot) {
        delete this.importErrorsByQuestion[id];
      }
    }
  }

  /** The extraction's own complaints about one question, kept separate from the live ones. */
  importErrorsFor(questionId: string): string[] {
    return this.importErrorsByQuestion[questionId] || [];
  }

  get importErrorCount(): number {
    let total = this.importGeneralErrors.length;
    for (const id of Object.keys(this.importErrorsByQuestion)) {
      total += this.importErrorsByQuestion[id].length;
    }
    return total;
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Preview
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  togglePreview(): void {
    this.showPreview = !this.showPreview;
  }

  onPreviewValueChange(answers: FormAnswers): void {
    this.previewAnswers = answers;
  }

  resetPreview(): void {
    this.previewAnswers = {};
    // A new identity, so the renderer's ngOnChanges sees a structural change and rebuilds.
    this.previewSchema = this.buildSchema();
  }

  /** Nothing to preview yet — say so rather than rendering an empty white box. */
  get previewIsEmpty(): boolean {
    if (!this.previewSchema) return true;
    return this.previewSchema.sections.every((s) => s.questions.length === 0);
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Publish
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /** The number the next publish will produce, for the button's label. Never a "Save". */
  get nextVersionLabel(): string {
    return 'Publish new version';
  }

  get isPublishBlockedLocally(): boolean {
    return this.clientErrors.length > 0;
  }

  /**
   * PUBLISH — `POST /custom-forms/:id/versions`.
   *
   * The client check runs FIRST so the practice sees its mistakes inline, against the question they
   * belong to, instead of as a round-trip. It is not a substitute for the server's: a pass here
   * sends the request, and a 400 `SCHEMA_INVALID` is displayed exactly as prominently. The two error
   * lists are kept apart so that can be seen to be true (see `serverErrors`).
   */
  async publish(): Promise<void> {
    if (!this.form) return;

    // Clear the PREVIOUS attempt's server verdict only. A stale "the server refused this" beside a
    // freshly edited schema is worse than none, and the request about to be sent will produce its
    // own.
    this.serverErrors = [];
    this.publishError = null;

    const schema = this.buildSchema();
    const local = validateSchema(schema);
    this.clientErrors = local.errors;
    this.applyErrorAttribution(local.errors);
    this.brokenConditions = this.detectBrokenConditions(schema);

    if (!local.valid) {
      this.showToastMessage(
        `This form cannot be published yet: ${local.errors.length} problem(s) to fix.`);
      return;
    }

    /*
     * THE LAST GATE ON AN EXTRACTION (§16.2 rule 1).
     *
     * Publishing is always the practice's own act, and it stays one press — but a draft nobody has
     * read yet is exactly the thing this feature must not send to a patient, and the person
     * pressing Publish may not be the person who ran the import. So a draft that came from a PDF
     * asks once, naming the document, before it becomes a version.
     */
    if (this.importedDraft) {
      const checked = await this.showConfirmationDialog(
        'Publish questions read from a PDF',
        `These questions were read out of "${this.importedDraft.sourceFileName}" by AI. Nobody has `
        + 'checked them unless you have. Publishing makes them the questions patients are asked, '
        + 'exactly as they are written below. Have you read every one against the original '
        + 'document?',
        undefined, 'Not yet', 'I have checked them — publish');
      if (!checked) return;
    }

    const isRepublish = !!this.form.currentVersionId;
    if (isRepublish) {
      // §13.2 / §14.1 decision 2: a version is immutable. Re-publishing does NOT edit the version
      // patients already hold — it writes a new one and repoints the form at it, and requests
      // already issued keep answering against the version they were pinned to. Saying so here is
      // the difference between a version history and a save button that happens to keep receipts.
      const confirmed = await this.showConfirmationDialog(
        'Publish a new version',
        `"${this.form.name}" already has a published version. Publishing creates a NEW version and `
        + 'makes it the one new patients receive. The existing version is not changed, and patients '
        + 'who already have a link keep answering the version they were sent.',
        undefined, 'Cancel', 'Publish new version');
      if (!confirmed) return;
    }

    this.isPublishing = true;

    this.customFormService.publishVersion(this.form.id, schema)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isPublishing = false;
          this.isDirty = false;
          this.dirtyChange.emit(false);
          this.showToastMessage(
            `Version ${response.data.version.versionNumber} published.`);
          this.published.emit({ version: response.data.version, form: response.data.form });
        },
        error: (error) => {
          this.isPublishing = false;
          const api = CustomFormService.asApiError(error);

          if (api && api.error === 'SCHEMA_INVALID') {
            /*
             * THE CASE THE CLIENT-SIDE CHECK MUST NOT BE ALLOWED TO HIDE.
             *
             * Reaching here means the local `validateSchema` passed and the server's did not. That
             * is either a version skew between the two implementations or a rule only the server
             * has — and in both cases the server is the authority, so its list is what the practice
             * is shown. Attributed to questions as well, so it lands beside the offending one.
             */
            this.serverErrors = (api.details && api.details.errors) || [api.message];
            this.applyErrorAttribution(this.serverErrors);
            this.showToastMessage(
              `The server refused this form: ${this.serverErrors.length} problem(s).`);
            return;
          }

          this.publishError = api
            ? api.message
            : 'This form could not be published. Please check your connection and try again.';
          this.showToastMessage(this.publishError);
        },
      });
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Small helpers
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /** For `*ngFor` over the form arrays, so a reorder moves DOM rather than rebuilding it. */
  trackByControl(index: number, control: AbstractControl): any {
    return control.get('id') ? control.get('id')!.value : index;
  }

  trackByIndex(index: number): number {
    return index;
  }

  private flatten(): { sectionIndex: number; questionIndex: number; group: FormGroup }[] {
    const flat: { sectionIndex: number; questionIndex: number; group: FormGroup }[] = [];
    for (let s = 0; s < this.sections.length; s++) {
      const questions = this.questionsIn(s);
      for (let q = 0; q < questions.length; q++) {
        flat.push({ sectionIndex: s, questionIndex: q, group: questions.at(q) as FormGroup });
      }
    }
    return flat;
  }

  private trimmed(value: any): string {
    return value === null || value === undefined ? '' : String(value).trim();
  }

  private numberOrNull(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const asNumber = Number(value);
    return Number.isFinite(asNumber) ? asNumber : null;
  }
}
